import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { Helmet } from "react-helmet";
import { useTranslation } from "@/hooks/useTranslation";
import { Button } from "@/components/ui/button";
import { User, Send, Check, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useCaregiverContacts, CaregiverContact } from "@/hooks/useCaregiverContacts";
import { toast } from "sonner";
import { SendSuccessOverlay } from "@/components/feedback/SendSuccessOverlay";

// Multi-language labels for Send Flow
const SEND_LABELS: Record<string, Record<string, string>> = {
  sendMessage:    { he: "שליחת הודעה", en: "Send Message", ru: "Отправить сообщение", ar: "إرسال رسالة" },
  message:        { he: "ההודעה:", en: "Message:", ru: "Сообщение:", ar: "الرسالة:" },
  noMessage:      { he: "(אין הודעה)", en: "(No message)", ru: "(Нет сообщения)", ar: "(لا رسالة)" },
  to:             { he: "למי:", en: "To:", ru: "Кому:", ar: "إلى:" },
  noContacts:     { he: "אין אנשי קשר", en: "No contacts", ru: "Нет контактов", ar: "لا جهات اتصال" },
  askCaregiver:   { he: "בקשו מהמטפל לסרוק את קוד ה-QR בהגדרות", en: "Ask your caregiver to scan the QR code in settings", ru: "Попросите ухаживающего отсканировать QR-код в настройках", ar: "اطلب من مقدم الرعاية مسح رمز QR في الإعدادات" },
  sendToWhom:     { he: "למי לשלוח?", en: "Send to whom?", ru: "Кому отправить?", ar: "إرسال لمن؟" },
  sendTheMessage: { he: "האם לשלוח את ההודעה?", en: "Send the message?", ru: "Отправить сообщение?", ar: "إرسال الرسالة؟" },
  sending:        { he: "שולח...", en: "Sending...", ru: "Отправка...", ar: "...جارٍ الإرسال" },
  sentSuccess:    { he: "נשלח בהצלחה!", en: "Sent successfully!", ru: "Отправлено!", ar: "!تم الإرسال بنجاح" },
  sendNow:        { he: "שלח עכשיו", en: "Send now", ru: "Отправить", ar: "أرسل الآن" },
  messageSent:    { he: "ההודעה נשלחה בהצלחה!", en: "Message sent successfully!", ru: "Сообщение отправлено!", ar: "!تم إرسال الرسالة بنجاح" },
  errorSending:   { he: "שגיאה בשליחת ההודעה", en: "Error sending message", ru: "Ошибка отправки", ar: "خطأ في الإرسال" },
};

export default function SendFlow() {
  const { t, dir, lang } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { contacts, isLoading: isLoadingContacts } = useCaregiverContacts();
  
  const [selectedContact, setSelectedContact] = useState<CaregiverContact | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const messageText = location.state?.text || sessionStorage.getItem("sendMessageText") || "";
  const isHebrew = dir === "rtl";
  const l = (key: string) => SEND_LABELS[key]?.[lang] || SEND_LABELS[key]?.en || key;

  const hasMultipleContacts = contacts.length > 1;
  const hasSingleContact = contacts.length === 1;

  // Auto-select if only one contact
  useEffect(() => {
    if (hasSingleContact && !selectedContact) {
      setSelectedContact(contacts[0]);
    }
  }, [hasSingleContact, selectedContact, contacts]);

  // Handle back navigation
  const handleBack = () => {
    if (selectedContact && hasMultipleContacts) {
      setSelectedContact(null);
    } else {
      navigate(-1);
    }
  };

  const handleSelectContact = (contact: CaregiverContact) => {
    setSelectedContact(contact);
  };

  const handleSend = async () => {
    if (!selectedContact) return;

    setIsSending(true);

    try {
      const { error } = await supabase.functions.invoke("send-push-alert", {
        body: {
          patientId: user?.id,
          alertType: "message",
          message: messageText,
        },
      });

      if (error) throw error;

      setIsSent(true);
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error(l("errorSending"));
      setIsSending(false);
    }
  };

  // Determine if we show contact selection or confirmation
  const showContactSelection = hasMultipleContacts && !selectedContact;

  const handleSuccessComplete = () => {
    sessionStorage.removeItem("sendMessageText");
    navigate("/", { replace: true });
  };

  if (isSent) {
    return <SendSuccessOverlay message={l("messageSent")} onComplete={handleSuccessComplete} />;
  }

  return (
    <>
      <Helmet>
        <title>{l("sendMessage")} - EyeControl App</title>
      </Helmet>

      <MainLayout onBack={handleBack}>
        <div className="flex-1 flex flex-col p-6 overflow-hidden" dir={dir}>
          {/* Page Title */}
          <h1 className={`text-2xl font-bold mb-4 ${isHebrew ? "text-right" : "text-left"}`}>
            {l("sendMessage")}
          </h1>

          {/* Message Preview - Always visible */}
          <div className="mb-4 p-3 bg-white dark:bg-muted/50 rounded-xl border-2 border-border">
            <p className={`text-sm text-muted-foreground mb-1 ${isHebrew ? "text-right" : "text-left"}`}>
              {l("message")}
            </p>
            <p className={`text-lg font-medium ${isHebrew ? "text-right" : "text-left"}`} dir={isHebrew ? "rtl" : "ltr"}>
              {messageText || l("noMessage")}
            </p>
          </div>

          {/* Selected Contact Box - Show when contact is selected */}
          <AnimatePresence>
            {selectedContact && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-6"
              >
                <div className="p-3 bg-white dark:bg-muted/50 rounded-xl border-2 border-border">
                  <p className={`text-sm text-muted-foreground mb-1 ${isHebrew ? "text-right" : "text-left"}`}>
                    {l("to")}
                  </p>
                  <div className={`flex items-center gap-2 ${isHebrew ? "justify-start" : "justify-end"}`}>
                    <User className="w-5 h-5 text-primary" />
                    <span className="font-medium">{selectedContact.nickname}</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Dynamic Content Based on State */}
          <div className="flex-1 flex flex-col">
            {isLoadingContacts ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : contacts.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center">
                <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-4">
                  <User className="w-10 h-10 text-muted-foreground" />
                </div>
                <p className="text-xl font-medium mb-2">
                  {l("noContacts")}
                </p>
                <p className="text-muted-foreground">
                  {l("askCaregiver")}
                </p>
              </div>
            ) : (
              <AnimatePresence mode="wait">
                {/* Contact Selection - Only if multiple contacts */}
                {showContactSelection && (
                  <motion.div
                    key="contact"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="flex-1 flex flex-col"
                  >
                    <div className={`mb-6 ${isHebrew ? "text-right" : "text-left"}`}>
                      <h2 className="text-xl font-bold">
                        {l("sendToWhom")}
                      </h2>
                    </div>

                    <div
                      className="w-full flex items-center justify-center gap-4 flex-wrap"
                      style={{ flex: "1 1 0", minHeight: 0 }}
                    >
                      {contacts.map((contact, index) => (
                        <motion.div
                          key={contact.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex-shrink-1"
                          style={{
                            width: `min(${Math.floor(90 / Math.max(contacts.length, 1))}%, 280px)`,
                            maxHeight: "100%",
                          }}
                        >
                          <Button
                            variant="outline"
                            onClick={() => handleSelectContact(contact)}
                            data-scan-item
                            className="w-full h-auto p-4 flex flex-col items-center gap-2 rounded-2xl border-2 hover:border-primary hover:bg-primary/10 transition-all"
                          >
                            <div
                              className="rounded-full bg-primary/20 flex items-center justify-center"
                              style={{ width: "clamp(3rem, 8vh, 5rem)", height: "clamp(3rem, 8vh, 5rem)" }}
                            >
                              <User style={{ width: "50%", height: "50%" }} className="text-primary" />
                            </div>
                            <div className="text-center">
                              <p className="font-semibold" style={{ fontSize: "clamp(0.85rem, 2vh, 1.15rem)" }}>{contact.nickname}</p>
                            </div>
                          </Button>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Confirm & Send - When contact is selected */}
                {selectedContact && (
                  <motion.div
                    key="confirm"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="flex-1 flex flex-col items-center justify-center"
                  >
                    <div className="w-full max-w-xl">
                      <div className={`mb-6 ${isHebrew ? "text-right" : "text-left"}`}>
                        <h2 className="text-xl font-bold">
                          {l("sendTheMessage")}
                        </h2>
                      </div>

                      <Button
                        onClick={handleSend}
                        disabled={isSending || isSent}
                        data-scan-item
                        className={`
                          w-full h-auto p-4 flex items-center justify-center gap-3 rounded-2xl
                          border-2 transition-all
                          ${isSent 
                            ? "bg-green-600 hover:bg-green-600 border-green-600" 
                            : "bg-primary hover:bg-primary/90 border-primary"
                          }
                        `}
                      >
                        {isSending ? (
                          <Loader2 className="w-6 h-6 animate-spin text-white" />
                        ) : isSent ? (
                          <Check className="w-6 h-6 text-white" />
                        ) : (
                          <Send className="w-6 h-6 text-white" />
                        )}
                        <span className="text-lg font-semibold text-white">
                          {isSending ? l("sending") : isSent ? l("sentSuccess") : l("sendNow")}
                        </span>
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            )}
          </div>
        </div>
      </MainLayout>
    </>
  );
}